import { Link } from 'react-router-dom';

export default function ContentSections() {
  return (
    <>
      <section id="what-is" className="py-16 md:py-20 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
            What Is Sugargoo Spreadsheet?
          </h2>
          <div className="prose-custom space-y-5 text-sm md:text-base text-foreground-700 leading-relaxed">
            <p>
              A <strong>Sugargoo Spreadsheet</strong> is a carefully curated product discovery tool used by savvy online shoppers to find trending fashion items, streetwear pieces, sneakers, accessories, and lifestyle products across global marketplaces. Instead of manually searching through endless product listings, users rely on these spreadsheets — updated daily by experienced community members — to quickly locate high-quality items with verified seller ratings, detailed QC photos, and accurate pricing information.
            </p>
            <p>
              The spreadsheet serves as a bridge between international shoppers and the vast catalogs of products available through Sugargoo&apos;s agent platform. Each entry typically includes a product name, direct purchase link, estimated price range, sizing notes, warehouse storage details, and community reviews. For beginners stepping into the world of agent-assisted shopping, a well-maintained <strong>Sugargoo Spreadsheet</strong> eliminates the guesswork and dramatically reduces the time spent hunting for reliable finds.
            </p>

            <div className="overflow-x-auto mt-8 rounded-lg border border-background-200/70">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-200/50">
                    <th className="px-4 py-3 text-left font-semibold text-foreground-800 whitespace-nowrap">Feature</th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground-800 whitespace-nowrap">Normal Search</th>
                    <th className="px-4 py-3 text-left font-semibold text-primary-700 whitespace-nowrap">Sugargoo Spreadsheet</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/70">
                  <tr>
                    <td className="px-4 py-3 text-foreground-700 font-medium whitespace-nowrap">Product Discovery</td>
                    <td className="px-4 py-3 text-foreground-600">Slow &amp; manual</td>
                    <td className="px-4 py-3 text-primary-700 font-medium whitespace-nowrap">Fast &amp; organised</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-foreground-700 font-medium whitespace-nowrap">Organisation</td>
                    <td className="px-4 py-3 text-foreground-600">Scattered listings</td>
                    <td className="px-4 py-3 text-primary-700 font-medium whitespace-nowrap">Structured categories</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-foreground-700 font-medium whitespace-nowrap">Beginner Friendly</td>
                    <td className="px-4 py-3 text-foreground-600">Steep learning curve</td>
                    <td className="px-4 py-3 text-primary-700 font-medium whitespace-nowrap">Yes, guided experience</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-foreground-700 font-medium whitespace-nowrap">Hidden Product Finds</td>
                    <td className="px-4 py-3 text-foreground-600">Difficult to uncover</td>
                    <td className="px-4 py-3 text-primary-700 font-medium whitespace-nowrap">Easy &amp; curated</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-foreground-700 font-medium whitespace-nowrap">Update Frequency</td>
                    <td className="px-4 py-3 text-foreground-600">Whenever you search</td>
                    <td className="px-4 py-3 text-primary-700 font-medium whitespace-nowrap">Updated daily</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="mt-6">
              Whether you are looking for the latest sneaker releases, premium streetwear brands, or affordable everyday basics, a <strong>Sugargoo Spreadsheet</strong> transforms the shopping experience from overwhelming to effortless. The spreadsheet format allows users to filter by category, compare prices across sellers, and make informed purchasing decisions before committing to an order through the Sugargoo platform.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-10 text-center">
            Why Use Sugargoo Spreadsheet?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {[
              {
                icon: 'ri-fire-line',
                title: 'Find Trending Products',
                desc: 'Stay ahead of fashion trends with daily updated spreadsheets featuring the hottest picks from community curators worldwide.',
              },
              {
                icon: 'ri-scales-line',
                title: 'Compare Sellers Easily',
                desc: 'Side-by-side product comparisons help you evaluate seller ratings, price points, and shipping options at a glance.',
              },
              {
                icon: 'ri-time-line',
                title: 'Save Shopping Time',
                desc: 'Skip hours of browsing — organised spreadsheets let you find exactly what you need in minutes, not hours.',
              },
              {
                icon: 'ri-search-eye-line',
                title: 'Discover Hidden Finds',
                desc: 'Access exclusive product discoveries and underground brands that are nearly impossible to find through regular search.',
              },
            ].map((item) => (
              <div
                key={item.title}
                className="group p-6 bg-background-100 rounded-lg hover:bg-primary-50 transition-all duration-300"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300 mb-4">
                  <i className={`${item.icon} text-lg`}></i>
                </span>
                <h3 className="text-base font-semibold text-foreground-900 mb-2 whitespace-nowrap">
                  {item.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="review" className="py-16 md:py-20 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
            Sugargoo Review: Is It Worth Using?
          </h2>
          <div className="prose-custom space-y-5 text-sm md:text-base text-foreground-700 leading-relaxed">
            <p>
              Sugargoo has established itself as one of the most reliable shopping agents for international buyers. The platform connects shoppers with a massive selection of products across multiple categories — from <strong>shoes and sneakers</strong> to <strong>hoodies, jackets, accessories, and more</strong>. Users consistently praise the combination of competitive pricing, responsive customer support, and detailed quality control photography that gives buyers peace of mind before shipping.
            </p>
            <p>
              The <strong>Sugargoo Spreadsheet</strong> ecosystem amplifies this experience by providing a community-driven layer of product discovery on top of the core agent service. Instead of relying solely on in-platform search, users can browse curated spreadsheets that highlight the best-reviewed items across every imaginable category. This dual-layer approach — agent service plus curated discovery — makes Sugargoo a standout choice for both beginners and experienced international shoppers.
            </p>

            <div className="overflow-x-auto mt-8 rounded-lg border border-background-200/70">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-200/50">
                    <th className="px-4 py-3 text-left font-semibold text-foreground-800 whitespace-nowrap">Category</th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground-800 whitespace-nowrap">Rating</th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground-800 whitespace-nowrap">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/70">
                  {[
                    { cat: 'Product Selection', rating: '★★★★★', note: 'Extensive catalog with diverse categories' },
                    { cat: 'Warehouse Service', rating: '★★★★☆', note: 'Reliable storage with QC photo options' },
                    { cat: 'Shipping Options', rating: '★★★★☆', note: 'Multiple carriers with competitive rates' },
                    { cat: 'User Experience', rating: '★★★★★', note: 'Intuitive platform with helpful guides' },
                    { cat: 'Customer Support', rating: '★★★★☆', note: 'Responsive team with multilingual assistance' },
                    { cat: 'Price Competitiveness', rating: '★★★★★', note: 'Fair agent fees compared to alternatives' },
                  ].map((row) => (
                    <tr key={row.cat}>
                      <td className="px-4 py-3 text-foreground-700 font-medium whitespace-nowrap">{row.cat}</td>
                      <td className="px-4 py-3 text-accent-500 whitespace-nowrap">{row.rating}</td>
                      <td className="px-4 py-3 text-foreground-600 text-xs md:text-sm">{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section id="shipping" className="py-16 md:py-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
            Sugargoo Shipping Cost Guide
          </h2>
          <div className="prose-custom space-y-5 text-sm md:text-base text-foreground-700 leading-relaxed">
            <p>
              Shipping costs through <strong>Sugargoo</strong> vary depending on several key factors: the total package weight, destination country, chosen shipping method, and package dimensions. Understanding how these variables interact can help you budget effectively and choose the most cost-efficient option for your haul. The Sugargoo platform provides real-time shipping estimates, but having a general framework helps when planning larger orders through your <strong>Sugargoo Spreadsheet</strong> finds.
            </p>

            <div className="overflow-x-auto mt-8 rounded-lg border border-background-200/70">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-200/50">
                    <th className="px-4 py-3 text-left font-semibold text-foreground-800 whitespace-nowrap">Package Weight</th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground-800 whitespace-nowrap">Estimated Cost (USA)</th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground-800 whitespace-nowrap">Estimated Cost (Europe)</th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground-800 whitespace-nowrap">Typical Items</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/70">
                  {[
                    { weight: 'Up to 1 kg', usa: '$15 — $30', eu: '$18 — $35', items: '1-2 t-shirts or accessories' },
                    { weight: '2 — 3 kg', usa: '$30 — $55', eu: '$35 — $60', items: '2-3 hoodies or a pair of shoes' },
                    { weight: '4 — 5 kg', usa: '$55 — $80', eu: '$60 — $90', items: 'Multiple clothing items + shoes' },
                    { weight: '6 — 10 kg', usa: '$80 — $140', eu: '$90 — $160', items: 'Full seasonal wardrobe haul' },
                  ].map((row) => (
                    <tr key={row.weight}>
                      <td className="px-4 py-3 text-foreground-700 font-medium whitespace-nowrap">{row.weight}</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{row.usa}</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{row.eu}</td>
                      <td className="px-4 py-3 text-foreground-600 text-xs md:text-sm">{row.items}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="mt-6 text-xs md:text-sm text-foreground-500">
              Note: Shipping costs are estimates and may vary based on carrier selection, seasonal demand, and promotional rates. Always check the Sugargoo platform for live pricing before finalising your order.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
            Is Sugargoo Legit? Safety &amp; Trust Guide
          </h2>
          <div className="prose-custom space-y-5 text-sm md:text-base text-foreground-700 leading-relaxed">
            <p>
              <strong>Yes, Sugargoo is a legitimate shopping agent platform</strong> trusted by thousands of international buyers worldwide. The company operates with transparent business practices, maintains secure warehouses for product inspection and storage, and offers buyer protection policies that safeguard your purchases throughout the entire process — from ordering to final delivery.
            </p>
            <p>
              Sugargoo employs several layers of security: verified seller partnerships reduce the risk of counterfeit items, detailed QC (Quality Control) photographs allow buyers to inspect products before international shipping, and secure payment gateways protect financial information. The platform also maintains active customer support channels that address concerns about order status, warehouse handling, and delivery tracking in real time.
            </p>
            <p>
              For users of the <strong>Sugargoo Spreadsheet</strong> ecosystem, the combination of community-vetted product links and Sugargoo&apos;s built-in buyer protections creates a shopping environment that significantly reduces common risks associated with cross-border e-commerce. The spreadsheet community actively flags unreliable sellers and shares updated information about product quality, making the entire experience both safer and more transparent than shopping independently.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}